import { Controller } from "@hotwired/stimulus"

import { positionPanel, trackPosition } from "../utils/popover_panel"
import { parseTime, snapTime, timesOfDay } from "../utils/time_format"

// Typeable time field with a themed dropdown of times.
//
// Native <input type="time"> was the obvious choice — it is typeable and locale-formatted —
// but its dropdown is a Chromium page-popup window that page CSS cannot reach, so its
// selection highlight is permanently blue. That is the same reason the native date picker was
// rejected, and it would have looked doubly odd sitting next to a green calendar.
//
// The input still accepts free-form entry ("7", "730", "19:45"); the list is only a shortcut,
// so times that are not on the step can still be typed.
export default class extends Controller {
  static targets = ["display", "value", "panel", "list", "trigger"]

  // 0 means do not snap; see TimeFieldComponent::DEFAULT_STEP.
  static values = { step: { type: Number, default: 0 } }

  connect() {
    this.reposition = () => positionPanel(this.displayTarget, this.panelTarget)
    this.stopTracking = null
    this.emitting = false
    // The list is built on first open, not here: at 15-minute suggestions that is 96 buttons
    // per field, and a timesheet form with break periods and multi-site driving rows carries a
    // dozen time fields plus more on every "add row" click.
    this.rendered = false
    // Another controller may have written the hidden value before this one
    // connected — break_rule_controller tops the Pause up on the form's first
    // events, multi_entry_controller fills a freshly cloned block — and the change
    // event it fired then reached no listener. The hidden field is what submits,
    // so the visible one follows it.
    this.valueChangedExternally()
  }

  disconnect() {
    this.stopTracking?.()
  }

  // --- opening and closing -------------------------------------------------

  toggle() {
    if (this.panelTarget.matches(":popover-open")) this.close()
    else this.open()
  }

  openFromInput() {
    if (this.panelTarget.matches(":popover-open")) return

    this.open()
  }

  open() {
    this.renderOptions()
    this.panelTarget.showPopover()
    this.reposition()
    this.stopTracking = trackPosition(this.reposition)
    this.scrollToSelected()
  }

  close() {
    if (this.panelTarget.matches(":popover-open")) this.panelTarget.hidePopover()
    this.stopTracking?.()
    this.stopTracking = null
  }

  panelToggled(event) {
    if (event.newState === "closed") {
      this.stopTracking?.()
      this.stopTracking = null
    }
  }

  // --- value plumbing ------------------------------------------------------

  displayChanged() {
    const parsed = parseTime(this.displayTarget.value)
    if (parsed) {
      this.writeValue(parsed)
      this.markSelected()
      this.displayTarget.setCustomValidity("")
    } else if (this.displayTarget.value.trim() === "") {
      this.writeValue("")
      this.displayTarget.setCustomValidity("")
    }
  }

  // On blur the entry is normalised and snapped onto the step, which is what the flatpickr
  // timepicker did. An unparseable entry is left visible to be corrected, not wiped.
  displayCommitted() {
    const raw = this.displayTarget.value.trim()

    if (raw === "") {
      this.writeValue("")
      this.displayTarget.setCustomValidity("")
      return
    }

    const snapped = snapTime(raw, this.stepValue)
    if (snapped) {
      this.displayTarget.value = snapped
      this.writeValue(snapped)
      this.displayTarget.setCustomValidity("")
    } else {
      this.displayTarget.setCustomValidity(this.invalidMessage)
    }
  }

  displayKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.displayCommitted()
      this.close()
    } else if (event.key === "Escape" && this.panelTarget.matches(":popover-open")) {
      event.preventDefault()
      this.close()
    }
  }

  selectOption(event) {
    const time = event.currentTarget.dataset.time

    this.displayTarget.value = time
    this.displayTarget.setCustomValidity("")
    this.writeValue(time)
    this.markSelected()
    this.close()
    this.displayTarget.focus()
  }

  // Something outside wrote to the hidden field and dispatched change.
  //
  // Our own writeValue() dispatches on the very field this listens to, so an echo has to be
  // ignored outright — otherwise the first keystroke that parses gets normalised back into the
  // input and the rest is appended to it, turning a typed "730" into "07:0030".
  //
  // Only the echo, though: a listener of our own change event may write the field again
  // inside that dispatch (break_rule_controller raising the Pause the employee just typed).
  // That value differs from the one we emitted, and dropping it left the hidden field
  // submitting a Pause the visible one did not show.
  valueChangedExternally() {
    if (this.emitting && this.valueTarget.value === this.emittedValue) return

    const time = this.valueTarget.value || ""
    if (this.displayTarget.value === time) return

    this.displayTarget.value = time
    this.displayTarget.setCustomValidity("")
    this.markSelected()
  }

  writeValue(time) {
    if (this.valueTarget.value === time) return

    this.valueTarget.value = time
    // Bracketed so our own valueChangedExternally can tell this round trip from a genuine
    // outside write. dispatchEvent is synchronous, so the flag covers exactly the echo.
    this.emittedValue = time
    this.emitting = true
    try {
      this.valueTarget.dispatchEvent(new Event("input", { bubbles: true }))
      this.valueTarget.dispatchEvent(new Event("change", { bubbles: true }))
    } finally {
      this.emitting = false
    }
  }

  // --- the list ------------------------------------------------------------

  renderOptions() {
    if (this.rendered || !this.hasListTarget) return
    this.rendered = true

    const fragment = document.createDocumentFragment()
    for (const time of timesOfDay(this.stepValue)) {
      const option = document.createElement("button")
      option.type = "button"
      option.dataset.time = time
      option.dataset.action = "time-picker#selectOption"
      option.className = "time-picker-option"
      option.textContent = time
      fragment.append(option)
    }

    this.listTarget.replaceChildren(fragment)
    this.markSelected()
  }

  // No-op until the list exists; open() renders it and marks the current value right after.
  markSelected() {
    if (!this.rendered) return

    const current = this.valueTarget.value
    for (const option of this.listTarget.children) {
      option.classList.toggle("is-selected", option.dataset.time === current)
    }
  }

  scrollToSelected() {
    const selected =
      this.listTarget.querySelector(".is-selected") ?? this.listTarget.firstElementChild
    selected?.scrollIntoView({ block: "center" })
  }

  get invalidMessage() {
    return this.displayTarget.dataset.invalidMessage || ""
  }
}
